import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { portfolioItems, services } from '../mock';
import { Card, CardContent } from '../components/ui/card';
import { Reveal } from '../components/motion/Reveal';
import { Lightbox, buildSlides } from '../components/Lightbox';
import { Images, ChevronsLeftRight, ZoomIn } from 'lucide-react';
import { Seo } from '../components/Seo';
import { seoPages } from '../seo';

const Portfolio = () => {
  const { t } = useLanguage();
  const [filter, setFilter] = useState('all');
  const [lightbox, setLightbox] = useState(null);

  const categories = [
    { value: 'all', label: { sl: 'Vse', en: 'All', it: 'Tutti' } },
    ...services.map((s) => ({ value: s.id, label: s.title }))
  ];

  const filteredItems = filter === 'all'
    ? portfolioItems
    : portfolioItems.filter((item) => item.category === filter);

  const openLightbox = (item) => setLightbox({ slides: buildSlides(item), title: t(item.title) });

  return (
    <div className="min-h-screen bg-[#000000] pt-32 pb-24">
      <Seo title={seoPages.portfolio.title} description={seoPages.portfolio.description} />
      <div className="container mx-auto px-5 md:px-10">
        <Reveal className="text-center mb-16">
          <h1 className="heading-2 text-[white] mb-6">
            {t({ sl: 'NAŠI PROJEKTI', en: 'OUR PROJECTS', it: 'I NOSTRI PROGETTI' })}
          </h1>
          <p className="body-large text-[#b0b0b0] max-w-3xl mx-auto">
            {t({
              sl: 'Oglejte si primere našega dela in se prepričajte o kakovosti naših storitev.',
              en: 'View examples of our work and see the quality of our services for yourself.',
              it: 'Visualizzate esempi del nostro lavoro e vedete voi stessi la qualità dei nostri servizi.'
            })}
          </p>
        </Reveal>

        <Reveal className="mb-10 md:mb-12" delay={0.1}>
          <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-5 px-5 md:mx-0 md:px-0 md:flex-wrap md:justify-center snap-x" data-testid="portfolio-filters">
            {categories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setFilter(cat.value)}
                className={`snap-start flex-shrink-0 min-h-[44px] px-4 md:px-5 rounded-full text-xs md:text-sm font-semibold uppercase tracking-wider whitespace-nowrap border transition-all duration-200 ${
                  filter === cat.value
                    ? 'bg-gradient-to-r from-cyan-400 via-fuchsia-500 to-yellow-400 text-black border-transparent shadow-[0_6px_20px_rgba(0,212,255,0.3)]'
                    : 'bg-white/5 border-white/15 text-[#d4d4d8] hover:border-white/40 hover:text-white'
                }`}
                style={{ fontFamily: "'Exo 2', sans-serif" }}
                data-testid={`filter-chip-${cat.value}`}
              >
                {t(cat.label)}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="portfolio-grid">
          {filteredItems.map((item, idx) => (
            <Reveal key={`${filter}-${item.id}`} delay={(idx % 3) * 0.08} y={32} className="h-full">
              <Card
                className="team-card bg-[#1a1a1a] border-[rgba(255, 255, 255, 0.1)] overflow-hidden cursor-pointer group h-full"
                onClick={() => openLightbox(item)}
                data-testid={`portfolio-card-${item.id}`}
              >
                <div className={`relative h-80 overflow-hidden ${item.whiteBg ? 'bg-white' : ''}`}>
                  <img
                    src={item.image}
                    alt={t(item.title)}
                    className={`w-full h-full transition-transform duration-500 group-hover:scale-110 ${item.whiteBg ? 'object-contain p-4' : 'object-cover'}`}
                  />
                  {!item.whiteBg && (
                    <div className="absolute inset-0 bg-gradient-to-t from-[#000000] via-transparent to-transparent opacity-60" />
                  )}
                  <div className="absolute top-3 right-3 flex gap-2">
                    {item.beforeAfter && (
                      <div className="bg-black/60 backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-1" data-testid={`before-after-badge-${item.id}`}>
                        <ChevronsLeftRight className="w-4 h-4 text-white" />
                        <span className="text-white text-sm font-medium">{t({ sl: 'Prej / Potem', en: 'Before / After', it: 'Prima / Dopo' })}</span>
                      </div>
                    )}
                    {item.gallery && item.gallery.length > 1 && (
                      <div className="bg-black/60 backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-1">
                        <Images className="w-4 h-4 text-white" />
                        <span className="text-white text-sm font-medium">{item.gallery.length}</span>
                      </div>
                    )}
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-4">
                      <ZoomIn className="w-8 h-8 text-white" />
                    </div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="heading-6 text-[white] mb-2">{t(item.title)}</h3>
                  <p className="body-small text-[#b0b0b0]">{t(item.description)}</p>
                </CardContent>
              </Card>
            </Reveal>
          ))}
        </div>

        {filteredItems.length === 0 && (
          <div className="text-center py-16">
            <p className="body-large text-[#b0b0b0]">
              {t({
                sl: 'Ni rezultatov za izbrano kategorijo.',
                en: 'No results for selected category.',
                it: 'Nessun risultato per la categoria selezionata.'
              })}
            </p>
          </div>
        )}
      </div>

      {lightbox && (
        <Lightbox slides={lightbox.slides} title={lightbox.title} onClose={() => setLightbox(null)} testId="portfolio-lightbox" />
      )}
    </div>
  );
};

export default Portfolio;
